import React, { useState, Component } from 'react';
import { TruckIcon, SearchIcon, FilterIcon, EyeIcon, PackageIcon, CheckCircleIcon, XCircleIcon, ClockIcon, BookIcon, BoxIcon, CalendarIcon, RefreshCwIcon, PlusIcon, ChevronDownIcon, AlertCircleIcon, UserIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// --- Types ---
type OrderStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
type ItemType = 'book' | 'merch' | 'bookbox' | 'event';
interface OrderItem {
  title: string;
  type: ItemType;
  quantity: number;
}
interface Order {
  id: string;
  customer: {
    name: string;
    initials: string;
  };
  items: OrderItem[];
  total: number;
  status: OrderStatus;
  tracking?: string;
  date: string;
  isLate?: boolean;
}
// --- Mock Data ---
const mockOrders: Order[] = [{
  id: 'ORD-001',
  customer: {
    name: 'John Smith',
    initials: 'JS'
  },
  items: [{
    title: 'The Silent Echo',
    type: 'book',
    quantity: 1
  }, {
    title: 'Echo Series T-Shirt',
    type: 'merch',
    quantity: 1
  }],
  total: 64.97,
  status: 'pending',
  date: '2023-10-15'
}, {
  id: 'ORD-002',
  customer: {
    name: 'Sarah Johnson',
    initials: 'SJ'
  },
  items: [{
    title: 'Whispers in the Dark',
    type: 'book',
    quantity: 1
  }],
  total: 12.99,
  status: 'shipped',
  tracking: '1Z999AA10123456784',
  date: '2023-10-14'
}, {
  id: 'ORD-003',
  customer: {
    name: 'Mike Wilson',
    initials: 'MW'
  },
  items: [{
    title: 'Beyond the Veil',
    type: 'book',
    quantity: 1
  }],
  total: 14.99,
  status: 'delivered',
  tracking: '1Z999AA10123456785',
  date: '2023-10-13'
}, {
  id: 'ORD-004',
  customer: {
    name: 'Emma Davis',
    initials: 'ED'
  },
  items: [{
    title: 'Fantasy Lovers Box',
    type: 'bookbox',
    quantity: 1
  }],
  total: 49.99,
  status: 'pending',
  date: '2023-10-12',
  isLate: true
}, {
  id: 'ORD-005',
  customer: {
    name: 'James Brown',
    initials: 'JB'
  },
  items: [{
    title: 'The Silent Echo',
    type: 'book',
    quantity: 1
  }, {
    title: 'Beyond the Veil',
    type: 'book',
    quantity: 1
  }],
  total: 27.98,
  status: 'processing',
  date: '2023-10-15'
}, {
  id: 'ORD-006',
  customer: {
    name: 'Lisa Anderson',
    initials: 'LA'
  },
  items: [{
    title: 'Echo Series Hoodie',
    type: 'merch',
    quantity: 1
  }],
  total: 24.99,
  status: 'cancelled',
  date: '2023-10-10'
}, {
  id: 'ORD-007',
  customer: {
    name: 'Tom Martinez',
    initials: 'TM'
  },
  items: [{
    title: 'The Silent Echo',
    type: 'book',
    quantity: 1
  }],
  total: 9.99,
  status: 'refunded',
  date: '2023-10-09'
}, {
  id: 'ORD-008',
  customer: {
    name: 'Amy Chen',
    initials: 'AC'
  },
  items: [{
    title: 'Whispers in the Dark',
    type: 'book',
    quantity: 1
  }, {
    title: 'Fantasy Lovers Box',
    type: 'bookbox',
    quantity: 1
  }],
  total: 64.98,
  status: 'delivered',
  tracking: '1Z999AA10123456786',
  date: '2023-10-08'
}];
// --- Helper Components ---
const StatusBadge = ({
  status
}: {
  status: OrderStatus;
}) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    processing: 'bg-blue-100 text-blue-800 border-blue-200',
    shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    delivered: 'bg-green-100 text-green-800 border-green-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    refunded: 'bg-gray-100 text-gray-800 border-gray-200'
  };
  const labels = {
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded'
  };
  const rt = useRoleTheme();
  return <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium border ${styles[status]}`} data-id="element-11655" data-mp="1jc9l0sno0o6c">
      {status === 'pending' && <ClockIcon className="w-3 h-3 mr-1" data-id="element-11656" data-mp="1aajsfji1rj6z" />}
      {status === 'processing' && <RefreshCwIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-11657" data-mp="vldlf9cywooj" />}
      {status === 'shipped' && <TruckIcon className="w-3 h-3 mr-1" data-id="element-11658" data-mp="10j5pf1qw0193" />}
      {status === 'delivered' && <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-11659" data-mp="10hcjjoo3ll25" />}
      {status === 'cancelled' && <XCircleIcon className="w-3 h-3 mr-1" data-id="element-11660" data-mp="mkpfz31n3wn9" />}
      {labels[status]}
    </span>;
};
const ItemIcon = ({
  type
}: {
  type: ItemType;
}) => {
  switch (type) {
    case 'book':
      return <BookIcon className="w-3 h-3 text-purple-500" data-id="element-11661" data-mp="7xqt7ldq9qev" />;
    case 'merch':
      return <BoxIcon className="w-3 h-3 text-amber-500" data-id="element-11662" data-mp="2z5tdn2e18qy" />;
    case 'bookbox':
      return <PackageIcon className="w-3 h-3 text-pink-500" data-id="element-11663" data-mp="gyex3n9eqae1" />;
    case 'event':
      return <CalendarIcon className="w-3 h-3 text-teal-500" data-id="element-11664" data-mp="cwbiiprfypoj" />;
    default:
      return <PackageIcon className="w-3 h-3 text-gray-500" data-id="element-11665" data-mp="cyej23q8017i" />;
  }
};
const Orders = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const [activeStatus, setActiveStatus] = useState<OrderStatus | 'all'>('all');
  const [dateFilter, setDateFilter] = useState('all_time');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter logic
  const filteredOrders = mockOrders.filter(order => {
    const matchesStatus = activeStatus === 'all' || order.status === activeStatus;
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customer.name.toLowerCase().includes(searchQuery.toLowerCase());
    // Date filter logic would go here in a real app
    return matchesStatus && matchesSearch;
  });
  // Stats
  const stats = {
    total: mockOrders.length,
    pending: mockOrders.filter(o => o.status === 'pending').length,
    shipped: mockOrders.filter(o => o.status === 'shipped').length,
    delivered: mockOrders.filter(o => o.status === 'delivered').length
  };
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-11666" data-mp="kzhz9r8no1v7">
      {/* Alert Banner */}
      {stats.pending > 0 && <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-id="element-11667" data-mp="pycphe8ycspc">
          <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-11668" data-mp="uyh9he92k5kb">
            <div className="flex items-center text-amber-800" data-id="element-11669" data-mp="z02gwk2u3rn4">
              <AlertCircleIcon className="h-5 w-5 mr-2" data-id="element-11670" data-mp="yzj93w6wejmt" />
              <span className="text-sm font-medium" data-id="element-11671" data-mp="2wqvmbywrajtv">
                {stats.pending} order{stats.pending > 1 ? 's are' : ' is'}{' '}
                awaiting fulfillment.
              </span>
            </div>
            <button onClick={() => setActiveStatus('pending')} className="text-sm font-medium text-amber-900 underline hover:text-amber-700" data-id="element-11672" data-mp="2t97x8c3461p1">
              View Pending
            </button>
          </div>
        </div>}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-11673" data-mp="2fh1kz1330euv">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-11674" data-mp="3c7286vnsyna2">
          <div data-id="element-11675" data-mp="377xv3txrc5eg">
            <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-11676" data-mp="377ftsiw8n0zo">
              Orders & Shipping
            </h1>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-11677" data-mp="335b1polfucf5">
              Manage your customer orders and shipments
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-11678" data-mp="1rrqhvcy9e8dk">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-11679" data-mp="1ms41wvr1qe12">
            <div className="flex items-center justify-between" data-id="element-11680" data-mp="1mqt1n5wt7oep">
              <div data-id="element-11681" data-mp="3mtaleckkmg4m">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11682" data-mp="3mssk31j1xbpu">
                  Total Orders
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-11683" data-mp="3cx7j96ppyhho">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-11684" data-mp="3hx3otae2egzc">
                <PackageIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-11685" data-mp="9fz815phyfvx" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-11686" data-mp="egmude2s68jt">
            <div className="flex items-center justify-between" data-id="element-11687" data-mp="3vuik2vr01iz8">
              <div data-id="element-11688" data-mp="4o7h2u2fi859">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11689" data-mp="4nfto6izjgq2">
                  Pending
                </p>
                <p className={`text-2xl font-bold mt-1 text-amber-600`} data-id="element-11690" data-mp="x5jsxu57nn7u">
                  {stats.pending}
                </p>
              </div>
              <div className="p-2 rounded-full bg-amber-100" data-id="element-11691" data-mp="2htm7kyahoolw">
                <ClockIcon className="h-5 w-5 text-amber-600" data-id="element-11692" data-mp="2cx5fjt065q3j" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-11693" data-mp="2qmh3h1e2h7z0">
            <div className="flex items-center justify-between" data-id="element-11694" data-mp="2mlx4rtduhwar">
              <div data-id="element-11695" data-mp="2mojfg1w18rx7">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11696" data-mp="1yw2tvhd36ht5">
                  Shipped
                </p>
                <p className={`text-2xl font-bold mt-1 text-indigo-600`} data-id="element-11697" data-mp="1tzukkklklitw">
                  {stats.shipped}
                </p>
              </div>
              <div className="p-2 rounded-full bg-indigo-100" data-id="element-11698" data-mp="22x4ua1iqozwp">
                <TruckIcon className="h-5 w-5 text-indigo-600" data-id="element-11699" data-mp="3jc64heqs180m" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-11700" data-mp="3eduvt7c7x3jz">
            <div className="flex items-center justify-between" data-id="element-11701" data-mp="2l5pwvwnf96em">
              <div data-id="element-11702" data-mp="2l4y2gp5k9epx">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11703" data-mp="2l7ka0osgbu85">
                  Delivered
                </p>
                <p className={`text-2xl font-bold mt-1 text-green-600`} data-id="element-11704" data-mp="2be3ftjoekd3y">
                  {stats.delivered}
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-11705" data-mp="22frf2ottnyxs">
                <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-11706" data-mp="22d59uvuelviq" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-11707" data-mp="1si3mo5jov3is">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between" data-id="element-11708" data-mp="1ogrzs7s78wea">
            {/* Status Tabs */}
            <div className="w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-11709" data-mp="3msqq624zshkx">
              <div className="flex space-x-2" data-id="element-11710" data-mp="3irnb3hurqpkr">
                {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'].map(status => <button key={status} onClick={() => setActiveStatus(status as OrderStatus | 'all')} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeStatus === status ? 'bg-purple-100 text-purple-700' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-11711" data-mp="1id3xgjumk6ck">
                    {status === 'all' ? 'All Orders' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>)}
              </div>
            </div>

            {/* Right Side: Date & Search */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-11712" data-mp="azjg06uoj0tr">
              <div className="relative min-w-[140px]" data-id="element-11713" data-mp="b1mfrilmw8bo">
                <select value={dateFilter} onChange={e => setDateFilter(e.target.value)} className={`w-full pl-3 pr-8 py-2 text-sm border rounded-md appearance-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-11714" data-mp="6ypv7mcja253">
                  <option value="all_time" data-id="element-11715" data-mp="3oez86qp6xkhi">All Time</option>
                  <option value="today" data-id="element-11716" data-mp="3jfuzs2a3bqs8">Today</option>
                  <option value="this_week" data-id="element-11717" data-mp="3jd9x5b1yx6tf">This Week</option>
                  <option value="this_month" data-id="element-11718" data-mp="1zwnybnub7dd">This Month</option>
                </select>
                <ChevronDownIcon className={`absolute right-2.5 top-2.5 h-4 w-4 pointer-events-none ${dk.textMuted}`} data-id="element-11719" data-mp="3td94x5qkct58" />
              </div>

              <div className="relative flex-grow sm:max-w-xs" data-id="element-11720" data-mp="30ova0yurucxw">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-11721" data-mp="30olfqz2p1jme">
                  <SearchIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-11722" data-mp="1g5hvom4x7yah" />
                </div>
                <input type="text" placeholder="Search orders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 ${dk.input} ${dk.border}`} data-id="element-11723" data-mp="1kyua63fvyopi" />
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredOrders.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-11724" data-mp="1uue3qj6z9bb3">
            <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-11725" data-mp="ejjtopol4rkm" />
            <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-11726" data-mp="ji4yl2es7zfe">
              No orders found
            </h3>
            <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-11727" data-mp="ysa0udh9yami">
              Try adjusting your search or filters.
            </p>
            <div className="mt-6" data-id="element-11728" data-mp="p048v3r0vt7b">
              <button onClick={() => {
            setActiveStatus('all');
            setSearchQuery('');
          }} className="text-sm font-medium text-purple-600 hover:text-purple-500" data-id="element-11729" data-mp="p16yz9gitrm6">
                Clear all filters
              </button>
            </div>
          </div> : <>
            {/* Desktop Table View */}
            <div className={`hidden md:block rounded-lg shadow-sm border overflow-hidden ${dk.card} ${dk.border}`} data-id="element-11730" data-mp="18mtifgjftc1v">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-11731" data-mp="3telxf6f0c7d0">
                <thead className={dk.bgMuted} data-id="element-11732" data-mp="3ofiqfgfgvr2e">
                  <tr data-id="element-11733" data-mp="3oehi3jpu5iod">
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11734" data-mp="3oe7olm5l0tdx">
                      Order #
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11735" data-mp="2cxmuwtuers7d">
                      Customer
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11736" data-mp="2mq2dntdj4ktb">
                      Items
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11737" data-mp="30o0par0gu354">
                      Total
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11738" data-mp="16bsowy5dku1y">
                      Status
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11739" data-mp="2ilk4l9urgqlt">
                      Tracking
                    </th>
                    <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-11740" data-mp="2sheves951rzo">
                      Date
                    </th>
                    <th scope="col" className={`relative px-6 py-3`} data-id="element-11741" data-mp="2x9y8lo3wyl3y">
                      <span className="sr-only" data-id="element-11742" data-mp="3b9pro39aerot">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 ${dk.card}`} data-id="element-11743" data-mp="36bxp8fb0nn6d">
                  {filteredOrders.map(order => <tr key={order.id} className={`hover:${dk.bgMuted}/50 transition-colors`} data-id="element-11744" data-mp="3uwaqhr1ak84f">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono ${dk.textHeading}`} data-id="element-11745" data-mp="3gycgsyxx0zv9">
                        {order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11746" data-mp="3bwww57zngvde">
                        <div className="flex items-center" data-id="element-11747" data-mp="3by7zx7t3i34i">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold mr-3" data-id="element-11748" data-mp="2x8sca0ov2dt1">
                            {order.customer.initials}
                          </div>
                          <div className={`text-sm font-medium ${dk.textHeading}`} data-id="element-11749" data-mp="2o8461s5plhqp">
                            {order.customer.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11750" data-mp="33qnc4gmp9ntg">
                        <div className="flex flex-col gap-1" data-id="element-11751" data-mp="2yq93awd9y9ud">
                          {order.items.map((item, idx) => <div key={idx} className={`text-xs flex items-center ${dk.textSecondary}`} data-id="element-11752" data-mp="2up742fjcxxse" data-mpi={idx}>
                              <span className="mr-1.5" data-id="element-11753" data-mp="2kycf33eq8k39">
                                <ItemIcon type={item.type} data-id="element-11754" data-mp="2kwrjdagoev3t" />
                              </span>
                              {item.quantity}x {item.title}
                            </div>)}
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${dk.textHeading}`} data-id="element-11755" data-mp="26xrwa78tao9r">
                        ${order.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11756" data-mp="hk1z9mpb72bn">
                        <StatusBadge status={order.status} data-id="element-11757" data-mp="mfpo20zvptgm" />
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-xs ${dk.textSecondary}`} data-id="element-11758" data-mp="qjp2me1lmi7c">
                        {order.tracking ? <span className="font-mono" data-id="element-11759" data-mp="3uxn9n13ui9zj">{order.tracking}</span> : <span className="text-gray-400 italic" data-id="element-11760" data-mp="2u365ycm2h31">
                            No tracking
                          </span>}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textSecondary}`} data-id="element-11761" data-mp="cj4jfgrrukdb">
                        {order.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11762" data-mp="3g6wh1qufj6vd">
                        <div className="flex items-center justify-end space-x-3" data-id="element-11763" data-mp="2nlgh0g172o1y">
                          <button className={`text-gray-400 hover:${dk.textHeading}`} title="View Details" data-id="element-11764" data-mp="2rnk3s0tbdf2j">
                            <EyeIcon className="h-4 w-4" data-id="element-11765" data-mp="31m13bm6bj207" />
                          </button>
                          {(order.status === 'pending' || order.status === 'processing') && <button className="text-indigo-600 hover:text-indigo-900" title="Mark Shipped" data-id="element-11766" data-mp="28wu56oas0efh">
                              <TruckIcon className="h-4 w-4" data-id="element-11767" data-mp="2inon5ggzorv4" />
                            </button>}
                          {order.status === 'pending' && <button className="text-red-400 hover:text-red-600" title="Cancel Order" data-id="element-11768" data-mp="1q6kok9e8sk49">
                              <XCircleIcon className="h-4 w-4" data-id="element-11769" data-mp="1dorusk6sjhoy" />
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4" data-id="element-11770" data-mp="zpsf4219ad4x">
              {filteredOrders.map(order => <div key={order.id} className={`relative rounded-lg shadow-sm border p-4 ${dk.card} ${dk.border} ${order.status === 'pending' ? 'border-l-4 border-l-amber-500' : ''}`} data-id="element-11771" data-mp="uwzarf9p96td">
                  <div className="flex justify-between items-start mb-3" data-id="element-11772" data-mp="kxqay5s5kxz3">
                    <div data-id="element-11773" data-mp="g089f0ggtl14">
                      <span className={`text-sm font-mono font-bold ${dk.textHeading}`} data-id="element-11774" data-mp="g2ujb6qtwcmi">
                        {order.id}
                      </span>
                      <div className="mt-1" data-id="element-11775" data-mp="1gdwowsp94rb">
                        <StatusBadge status={order.status} data-id="element-11776" data-mp="6de1739whzgb" />
                      </div>
                    </div>
                    <span className={`text-xs ${dk.textMuted}`} data-id="element-11777" data-mp="3ok26ou8v4omi">
                      {order.date}
                    </span>
                  </div>

                  <div className="flex items-center mb-3 pb-3 border-b dark:border-gray-700" data-id="element-11778" data-mp="l20gj4zj2r7j">
                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold mr-3" data-id="element-11779" data-mp="l4ecrh5y29go">
                      {order.customer.initials}
                    </div>
                    <span className={`text-sm font-medium ${dk.textHeading}`} data-id="element-11780" data-mp="gb1vxt7ib6yh">
                      {order.customer.name}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4" data-id="element-11781" data-mp="13z1mas0wovcj">
                    {order.items.map((item, idx) => <div key={idx} className={`text-sm flex items-center ${dk.textSecondary}`} data-id="element-11782" data-mp="1i6eqztuir4sq" data-mpi={idx}>
                        <span className="mr-2" data-id="element-11783" data-mp="1e4kttvjj8ixb">
                          <ItemIcon type={item.type} data-id="element-11784" data-mp="1rwr6v8re29sj" />
                        </span>
                        <span className="font-medium mr-1" data-id="element-11785" data-mp="1mxemqvrwjgtn">
                          {item.quantity}x
                        </span>
                        <span className="truncate" data-id="element-11786" data-mp="z8lkyiolmer4">{item.title}</span>
                      </div>)}
                  </div>

                  <div className="flex items-center justify-between pt-2" data-id="element-11787" data-mp="v81ntf40zb4z">
                    <span className={`text-lg font-bold ${dk.textHeading}`} data-id="element-11788" data-mp="197b5ykbskfbl">
                      ${order.total.toFixed(2)}
                    </span>
                    <div className="flex gap-2" data-id="element-11789" data-mp="2kkn5wk4fkdsh">
                      <button className={`p-2 rounded-md border ${dk.border} ${dk.textSecondary} hover:${dk.bgMuted}`} data-id="element-11790" data-mp="2kj27gjezwapc">
                        <EyeIcon className="h-4 w-4" data-id="element-11791" data-mp="p5hvnkg061sg" />
                      </button>
                      {(order.status === 'pending' || order.status === 'processing') && <button className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 flex items-center" data-id="element-11792" data-mp="z1tdsr29xlut">
                          <TruckIcon className="h-3 w-3 mr-1.5" data-id="element-11793" data-mp="1324xo1mtbi58" />
                          Ship
                        </button>}
                    </div>
                  </div>
                </div>)}
            </div>
          </>}
      </div>
    </div>;
};
export default Orders;